"use client";

import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import messages from "@/i18n/messages/en.json";

import { useState } from "react";
import {
  BriefcaseBusiness,
  FolderKanban,
  GraduationCap,
  Pencil,
  Plus,
} from "lucide-react";

import { ProfileDescription } from "../presentation/ProfileDescription";
import { ProfileEditorPopup } from "../editors/ProfileEditorPopup";
import { ProfileSectionCard } from "../presentation/ProfileSectionCard";
import {
  PROFILE_CARD_ACTION_ICON_CLASS_NAME,
  PROFILE_CARD_EDIT_CLASS_NAME,
  PROFILE_CARD_ICON_CLASS_NAME,
  PROFILE_CARD_VISIBLE_ENTRIES,
  PROFILE_HISTORY_CONFIG,
} from "../profileCards.constants";
import {
  profilePeriod,
  safeProfileLink,
  profileHistoryGroups,
  profileEmploymentType,
} from "../profileCards.utils";
import type { ProfileHistorySectionProps } from "../profileCards.types";

import { ProfileHistoryEditor } from "../editors/ProfileHistoryEditor";
export function ProfileHistorySection({
  kind,
  entries,
  editor,
  disabled,
  readOnly,
  onEdit,
  onCancel,
  onChange,
}: ProfileHistorySectionProps) {
  const [expanded, setExpanded] = useState(false);
  const config = PROFILE_HISTORY_CONFIG[kind];
  const editing = editor?.section === kind;
  const groups = profileHistoryGroups(entries, kind);
  const visible = expanded
    ? groups
    : groups.slice(0, PROFILE_CARD_VISIBLE_ENTRIES);
  const Icon =
    kind === "experience"
      ? BriefcaseBusiness
      : kind === "education"
        ? GraduationCap
        : FolderKanban;

  return (
    <>
      <ProfileSectionCard
        title={config.title}
        action={
          readOnly ? undefined : (
            <button
              type="button"
              aria-label={talentNetworkText("add_entry", {
                entry: config.singular,
              })}
              className={PROFILE_CARD_EDIT_CLASS_NAME}
              disabled={
                disabled ||
                (editor !== null &&
                  (editor.section !== kind || editor.index !== undefined)) ||
                entries.length >= config.limit
              }
              onClick={() => onEdit?.()}
            >
              <Plus
                aria-hidden="true"
                className={PROFILE_CARD_ACTION_ICON_CLASS_NAME}
              />
            </button>
          )
        }
      >
        {entries.length ? (
          <div className="divide-y divide-[#EAECF0]">
            {visible.map((group) => (
              <div
                key={group.entries[0].index}
                className="py-4 first:pt-0 last:pb-0"
              >
                {group.entries.length > 1 ? (
                  <div className="mb-3 flex items-center gap-3">
                    <span className={PROFILE_CARD_ICON_CLASS_NAME}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="min-w-0 break-words text-sm font-semibold text-[#101828]">
                      {group.company}
                    </h3>
                  </div>
                ) : null}
                <div
                  className={
                    group.entries.length > 1
                      ? "ml-[17px] border-l border-[#D0D5DD] pl-7"
                      : ""
                  }
                >
                  {group.entries.map(({ entry, index }) => {
                    const title = String(
                      entry[config.primary] ||
                        entry[config.secondary] ||
                        config.title,
                    );
                    const period = profilePeriod(entry);
                    const url = safeProfileLink(entry.url);
                    return (
                      <article
                        key={index}
                        className="flex gap-3 py-4 first:pt-0 last:pb-0"
                      >
                        {group.entries.length === 1 ? (
                          <span className={PROFILE_CARD_ICON_CLASS_NAME}>
                            <Icon className="h-5 w-5" />
                          </span>
                        ) : null}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-1">
                            <h3 className="break-words text-sm font-semibold leading-5 text-[#101828]">
                              {title}
                            </h3>
                            {!readOnly ? (
                              <button
                                type="button"
                                className={`${PROFILE_CARD_EDIT_CLASS_NAME} -mt-1`}
                                aria-label={talentNetworkText(
                                  "edit_named_entry",
                                  { entry: config.singular, name: title },
                                )}
                                disabled={
                                  disabled ||
                                  (editor !== null &&
                                    (editor.section !== kind ||
                                      editor.index !== index))
                                }
                                onClick={() => onEdit?.(index)}
                              >
                                <Pencil
                                  aria-hidden="true"
                                  className={
                                    PROFILE_CARD_ACTION_ICON_CLASS_NAME
                                  }
                                />
                              </button>
                            ) : null}
                          </div>
                          {kind === "experience" ? (
                            <p className="text-xs leading-5 text-[#344054]">
                              {[
                                group.entries.length === 1
                                  ? entry.company
                                  : undefined,
                                profileEmploymentType(entry.employmentType),
                              ]
                                .filter(Boolean)
                                .join(" \u00b7 ")}
                            </p>
                          ) : null}
                          {kind === "education" ? (
                            <p className="text-xs leading-5 text-[#344054]">
                              {[entry.level, entry.field]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          ) : null}
                          {period ? (
                            <p className="text-xs leading-5 text-[#667085]">
                              {period}
                            </p>
                          ) : null}
                          {kind === "experience" && entry.location ? (
                            <p className="text-xs leading-5 text-[#667085]">
                              {entry.location}
                            </p>
                          ) : null}
                          {kind === "education" && entry.grade ? (
                            <p className="mt-1 text-xs text-[#475467]">
                              {messages.talentNetworkUi.grade}
                              {entry.grade}
                            </p>
                          ) : null}
                          {kind === "projects" && entry.organization ? (
                            <p className="mt-1 text-xs text-[#667085]">
                              {messages.talentNetworkUi.associated_with}
                              {entry.organization}
                            </p>
                          ) : null}
                          {entry.description ? (
                            <ProfileDescription
                              key={entry.description}
                              description={entry.description}
                            />
                          ) : null}
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-block text-xs font-semibold text-[#344054] underline"
                            >
                              {messages.talentNetworkUi.view_project}
                            </a>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs leading-5 text-[#667085]">
            {readOnly
              ? talentNetworkText("no_entries", {
                  entries: config.title.toLowerCase(),
                })
              : talentNetworkText("empty_history", {
                  entries: config.title.toLowerCase(),
                  article: config.singular === "project" ? "a" : "your",
                  entry: config.singular,
                })}
          </p>
        )}
        {groups.length > PROFILE_CARD_VISIBLE_ENTRIES ? (
          <button
            type="button"
            className="mt-4 w-full border-t border-[#EAECF0] pt-3 text-xs font-semibold text-[#475467]"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? messages.talentNetworkUi.show_less
              : talentNetworkText("show_all_entries", {
                  count: entries.length,
                  entries: config.plural,
                })}
          </button>
        ) : null}
      </ProfileSectionCard>
      {!readOnly && onCancel && onChange ? (
        <ProfileEditorPopup
          isOpen={editing}
          title={talentNetworkText(
            editor?.index === undefined ? "add_entry" : "edit_entry",
            { entry: config.singular },
          )}
          onClose={onCancel}
        >
          {editing ? (
            <ProfileHistoryEditor
              key={editor.index ?? "new"}
              entry={editor.index === undefined ? {} : entries[editor.index]}
              config={config}
              isNew={editor.index === undefined}
              onCancel={onCancel}
              onSave={(entry) => {
                onChange(
                  editor.index === undefined
                    ? [...entries, entry]
                    : entries.map((item, index) =>
                        index === editor.index ? entry : item,
                      ),
                );
                setExpanded(true);
              }}
              onRemove={() =>
                onChange(entries.filter((_, index) => index !== editor.index))
              }
            />
          ) : null}
        </ProfileEditorPopup>
      ) : null}
    </>
  );
}
